/**
 * Extracted from AdminRestaurants.jsx — the create/edit Shop modal
 * body (form fields + verticals picker + map + GST/contact/commission
 * override). State + handlers stay in the parent; this is purely a
 * controlled-input view layer.
 *
 * Why split: the parent had ~620 lines, most of which was this form's
 * 220-line JSX. Pulling it out keeps state colocated where the
 * load/save side-effects live, but stops the file from being unreadable.
 */
import { useMemo, useRef, useState } from "react";
import { Check, ChevronDown, MapPin, Search } from "lucide-react";
import ImageUploader from "../../../components/ImageUploader";
import ShopMapPicker from "../../../components/ShopMapPicker";
import { toggleVerticalSelection, useViewportSettled } from "../../../lib/verticalPicker";

export function Field({ label, children, full }) {
    return (
        <label className={`flex flex-col gap-1 ${full ? "col-span-2" : ""}`}>
            <span className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">{label}</span>
            {children}
        </label>
    );
}

// Sentinel for "let the backend derive the zone from the map pin" — the
// behaviour every shop had before this dropdown existed. Deliberately not
// "" (that means an explicit "Unassigned") and not null/undefined (those
// are easy to produce by accident and would silently change behaviour).
export const ZONE_AUTO = "__AUTO__";

/** Searchable delivery-zone picker.
 *
 * Hand-rolled rather than the Shadcn Command/Popover combobox because
 * this form renders inside the page's own (non-Radix) ``Modal`` — a
 * portalled Radix popover there brings focus-trap and z-index problems
 * for no benefit at the handful-of-zones scale a tenant's plan allows.
 */
function ZonePicker({ zones, value, onChange }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const boxRef = useRef(null);

    const selected = zones.find((z) => z.id === value);
    const label =
        value === ZONE_AUTO ? "Auto — assign from map pin"
            : !value ? "Unassigned"
                : selected ? selected.name
                    // Pinned to a zone that no longer exists (deleted after
                    // assignment) — say so instead of rendering a blank box.
                    : "Unknown zone (no longer exists)";

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        if (!needle) return zones;
        return zones.filter((z) => (z.name || "").toLowerCase().includes(needle));
    }, [zones, q]);

    const pick = (v) => { onChange(v); setOpen(false); setQ(""); };

    return (
        <div
            ref={boxRef}
            className="relative"
            data-testid="shop-zone-picker"
            onBlur={(e) => {
                // Close only when focus actually leaves the whole widget —
                // moving between the trigger and the search box must not.
                if (!boxRef.current?.contains(e.relatedTarget)) setOpen(false);
            }}
        >
            <button
                type="button"
                data-testid="shop-zone-trigger"
                onClick={() => setOpen((o) => !o)}
                className="inp w-full flex items-center justify-between gap-2 text-left"
            >
                <span className={!value && value !== ZONE_AUTO ? "text-muted-foreground" : ""}>{label}</span>
                <ChevronDown size={14} className="shrink-0 text-muted-foreground" />
            </button>

            {open && (
                <div className="absolute z-50 mt-1 w-full rounded-xl border border-border bg-card shadow-lg overflow-hidden">
                    <div className="flex items-center gap-2 px-2.5 py-2 border-b border-border">
                        <Search size={13} className="text-muted-foreground shrink-0" />
                        <input
                            autoFocus
                            data-testid="shop-zone-search"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search zones…"
                            className="w-full bg-transparent text-sm outline-none"
                        />
                    </div>
                    <div className="max-h-56 overflow-y-auto py-1">
                        <Opt testid="shop-zone-option-auto" active={value === ZONE_AUTO} onPick={() => pick(ZONE_AUTO)}
                            title="Auto — assign from map pin"
                            sub="Uses the smallest delivery zone containing the shop's location" />
                        <Opt testid="shop-zone-option-none" active={value === ""} onPick={() => pick("")}
                            title="Unassigned" sub="No delivery zone for this shop" />
                        {filtered.length > 0 && <div className="my-1 border-t border-border" />}
                        {filtered.map((z) => (
                            <Opt
                                key={z.id}
                                testid={`shop-zone-option-${z.id}`}
                                active={value === z.id}
                                onPick={() => pick(z.id)}
                                title={z.name}
                                sub={`${z.radius_km ?? "?"} km radius${z.status && z.status !== "ACTIVE" ? ` · ${z.status}` : ""}`}
                            />
                        ))}
                        {filtered.length === 0 && q.trim() && (
                            <p className="px-3 py-3 text-xs text-muted-foreground">No zone matches “{q.trim()}”.</p>
                        )}
                        {zones.length === 0 && (
                            <p className="px-3 py-3 text-xs text-muted-foreground">
                                No delivery zones yet — create one under Admin → Zones.
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function Opt({ testid, active, onPick, title, sub }) {
    return (
        <button
            type="button"
            data-testid={testid}
            onClick={onPick}
            className={`w-full text-left px-3 py-2 hover:bg-muted flex items-start gap-2 ${active ? "bg-muted/60" : ""}`}
        >
            <Check size={13} className={`mt-0.5 shrink-0 ${active ? "text-primary" : "opacity-0"}`} />
            <span className="min-w-0">
                <span className="block text-sm font-semibold truncate">{title}</span>
                {sub && <span className="block text-[11px] text-muted-foreground truncate">{sub}</span>}
            </span>
        </button>
    );
}

export default function ShopForm({
    form, setForm, edit, showFoodType, parentVerticals, subVerticalsBy, zones = [],
}) {
    const settled = useViewportSettled();
    return (
        <div className="grid grid-cols-2 gap-3 text-sm">
            <Field full label="Name">
                <input data-testid="form-rest-name" className="inp" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field full label="Description">
                <textarea className="inp" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Field>
            <Field full label="Search keywords (comma separated)">
                <input className="inp" value={form.cuisines} onChange={(e) => setForm({ ...form, cuisines: e.target.value })} placeholder="Biryani, Mughlai · or · Plumbing, Repair" />
                <span className="text-[11px] text-muted-foreground mt-1">Helps customers find this shop in search.</span>
            </Field>
            <Field full label="Vertical / Business category">
                <div className="space-y-2.5" data-testid="vertical-picker">
                    {parentVerticals.map((v) => {
                        const pid = v.id || v.slug;
                        const subs = subVerticalsBy[pid] || [];
                        const onP = (form.business_category_ids || []).includes(pid);
                        return (
                            <div key={pid}>
                                <button
                                    type="button"
                                    data-testid={`vertical-${pid}`}
                                    onClick={() => {
                                        // Ambiguous while the keyboard is collapsing — see lib/verticalPicker.js.
                                        if (!settled()) return;
                                        setForm({ ...form, business_category_ids: toggleVerticalSelection(form.business_category_ids, pid) });
                                    }}
                                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition border ${onP ? "bg-primary text-primary-foreground border-primary" : "bg-muted text-muted-foreground border-border hover:border-primary"}`}
                                >{v.title || v.name}</button>
                                {subs.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mt-1.5 ml-3">
                                        {subs.map((s) => {
                                            const sid = s.id || s.slug;
                                            const onS = (form.business_category_ids || []).includes(sid);
                                            return (
                                                <button
                                                    key={sid}
                                                    type="button"
                                                    data-testid={`sub-vertical-${sid}`}
                                                    onClick={() => {
                                                        if (!settled()) return;
                                                        // Selecting a subcategory auto-includes its parent
                                                        setForm({ ...form, business_category_ids: toggleVerticalSelection(form.business_category_ids, sid, { parentId: pid }) });
                                                    }}
                                                    className={`text-[11px] rounded-full px-2.5 py-1 border ${onS ? "bg-primary/15 text-primary border-primary/40" : "bg-card border-border text-muted-foreground"}`}
                                                >{s.title || s.name}</button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    {parentVerticals.length === 0 && (
                        <span className="text-xs text-muted-foreground" data-testid="verticals-empty-hint">
                            No verticals available yet. Create one in Admin → Categories.
                        </span>
                    )}
                </div>
                <span className="text-[11px] text-muted-foreground mt-2">A shop appears under each vertical (and subcategory) you pick. Pick one or more.</span>
            </Field>
            {showFoodType && (
                <Field label="Type">
                    <select className="inp" value={form.food_type === "OTHER" ? "VEG_NON_VEG" : form.food_type} onChange={(e) => setForm({ ...form, food_type: e.target.value })}>
                        <option>VEG</option><option>NON_VEG</option><option>VEG_NON_VEG</option>
                    </select>
                </Field>
            )}
            <Field label="Min order ₹">
                <input type="number" className="inp" value={form.min_order_value} onChange={(e) => setForm({ ...form, min_order_value: e.target.value })} />
            </Field>
            <Field full label={<span className="inline-flex items-center gap-1.5"><MapPin size={13} /> Shop location on map</span>}>
                <ShopMapPicker
                    value={form.lat != null && form.lng != null ? { lat: form.lat, lng: form.lng } : null}
                    onChange={({ lat, lng, city, pincode, displayName, userInitiated }) => {
                        setForm((f) => ({
                            ...f,
                            lat,
                            lng,
                            // iter126 — when the change was user-initiated
                            // (search-select, drag, map click, "use current"),
                            // OVERWRITE the address fields so the picked
                            // location is reflected. When the change fires
                            // from the mount-time reverse-geocode (opening
                            // an existing shop), fall back to the stored
                            // value so we don't clobber the admin's edits.
                            address_city: userInitiated
                                ? (city || "")
                                : (f.address_city || city || ""),
                            address_pincode: userInitiated
                                ? (pincode || "")
                                : (f.address_pincode || pincode || ""),
                            address_line1: userInitiated
                                ? (displayName ? displayName.split(",").slice(0, 2).join(", ") : "")
                                : (f.address_line1 || (displayName ? displayName.split(",").slice(0, 2).join(", ") : "")),
                        }));
                    }}
                />
            </Field>
            <Field label="Address line"><input className="inp" value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} placeholder="Building / door / street" /></Field>
            <Field label="Pincode"><input className="inp" value={form.address_pincode} onChange={(e) => setForm({ ...form, address_pincode: e.target.value })} placeholder="500034" /></Field>
            <Field full label="Delivery zone">
                <ZonePicker
                    zones={zones}
                    value={form.zone_id}
                    onChange={(zone_id) => setForm((f) => ({ ...f, zone_id }))}
                />
                <span className="text-[11px] text-muted-foreground mt-1">
                    Pick a zone to pin this shop to it regardless of the map pin. On
                    “Auto”, the zone is derived from the location above whenever the
                    pin moves.
                </span>
            </Field>
            <Field full label="Cover image">
                <ImageUploader value={form.cover_image_url} onUploaded={(url) => setForm({ ...form, cover_image_url: url })} aspect="aspect-[16/9]" testId="shop-cover-image" />
            </Field>
            <Field full label="Shop logo">
                <ImageUploader value={form.logo_url} onUploaded={(url) => setForm({ ...form, logo_url: url })} aspect="aspect-square" testId="shop-logo" className="max-w-[200px]" />
            </Field>
            {edit && (
                <Field label="Status">
                    <select className="inp" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                        <option value="ACTIVE">ACTIVE</option>
                        <option value="INACTIVE">INACTIVE</option>
                    </select>
                </Field>
            )}
            <Field full label="">
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer mt-1">
                    <input data-testid="cdc-toggle" type="checkbox" className="accent-primary w-4 h-4" checked={!!form.cdc_enabled} onChange={(e) => setForm({ ...form, cdc_enabled: e.target.checked })} />
                    <span>
                        <span className="font-semibold">Cash-During-Collection (CDC)</span>
                        <span className="block text-xs text-muted-foreground">Driver pays the restaurant the merchant payout in cash on pickup, then collects from the customer at delivery.</span>
                    </span>
                </label>
            </Field>
            <Field full label="Visibility">
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer mt-1">
                    <input
                        data-testid="form-shop-is-featured"
                        type="checkbox"
                        className="accent-primary w-4 h-4"
                        checked={!!form.is_featured}
                        onChange={(e) => setForm({ ...form, is_featured: e.target.checked })}
                    />
                    <span>
                        <span className="font-semibold">Show in &quot;Featured&quot; on the home page</span>
                        <span className="block text-xs text-muted-foreground">Featured shops appear in a dedicated row at the top of the customer home page with a FEATURED badge. Use to highlight your top performers or new launches.</span>
                    </span>
                </label>
            </Field>
            <Field full label="">
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer mt-1">
                    <input
                        data-testid="contact-visible-toggle"
                        type="checkbox"
                        className="accent-primary w-4 h-4"
                        checked={!!form.contact_visible_to_customer}
                        onChange={(e) => setForm({ ...form, contact_visible_to_customer: e.target.checked })}
                    />
                    <span>
                        <span className="font-semibold">Show &quot;Contact merchant&quot; on customer orders</span>
                        <span className="block text-xs text-muted-foreground">When on, customers see this shop&apos;s mobile number with a Call button on the order track page.</span>
                    </span>
                </label>
            </Field>
            {!edit && (
                <Field full label="Referral code (optional)">
                    <input
                        data-testid="form-rest-referral-code"
                        className="inp font-mono uppercase tracking-wider"
                        value={form.referral_code}
                        onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase() })}
                        placeholder="BIZ-XXXXXX"
                    />
                    <span className="text-[11px] text-muted-foreground mt-1">If a customer referred this shop, paste their code to attribute future earnings.</span>
                </Field>
            )}
            <Field full label="Services offered">
                <div className="space-y-2 mt-1">
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                        <input data-testid="hd-toggle" type="checkbox" className="accent-primary w-4 h-4"
                            checked={form.home_delivery_enabled !== false}
                            onChange={(e) => setForm({ ...form, home_delivery_enabled: e.target.checked })} />
                        <span className="font-semibold">Home Delivery</span>
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                        <input data-testid="pickup-toggle" type="checkbox" className="accent-primary w-4 h-4"
                            checked={form.pickup_enabled !== false}
                            onChange={(e) => setForm({ ...form, pickup_enabled: e.target.checked })} />
                        <span className="font-semibold">Store Pickup / Take Away</span>
                    </label>
                </div>
            </Field>

            {/* Phase 2 — Contacts (optional) */}
            <Field label="Shop phone">
                <input data-testid="form-shop-phone" type="tel" className="inp"
                    value={form.mobile_number}
                    onChange={(e) => setForm({ ...form, mobile_number: e.target.value })}
                    placeholder="e.g. 9000000000" />
            </Field>
            <Field label="WhatsApp">
                <input data-testid="form-shop-whatsapp" type="tel" className="inp"
                    value={form.whatsapp_number}
                    onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })}
                    placeholder="Defaults to shop phone if blank" />
            </Field>
            <Field full label="Email">
                <input data-testid="form-shop-email" type="email" className="inp"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="shop@example.com" />
            </Field>

            {/* Phase 2 — GST identity (optional) */}
            <Field label="GSTIN">
                <input data-testid="form-shop-gstin" className="inp uppercase font-mono"
                    value={form.gst_number}
                    onChange={(e) => setForm({ ...form, gst_number: e.target.value.toUpperCase() })}
                    placeholder="22AAAAA0000A1Z5" />
            </Field>
            <Field label="GST Certificate">
                <ImageUploader
                    value={form.gst_certificate_url}
                    onUploaded={(url) => setForm({ ...form, gst_certificate_url: url })}
                    aspect="aspect-[4/3]"
                    testId="shop-gst-certificate"
                    acceptPdf
                />
            </Field>

            {/* iter62 — Free delivery for this shop (overridden by Zone setting when enabled) */}
            <Field full label="Free delivery">
                <div className="flex items-center gap-3 flex-wrap mt-1">
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                        <input
                            data-testid="form-shop-free-delivery-enabled"
                            type="checkbox"
                            className="accent-primary w-4 h-4"
                            checked={!!form.free_delivery_enabled}
                            onChange={(e) => setForm({ ...form, free_delivery_enabled: e.target.checked })}
                        />
                        <span className="font-semibold">{form.free_delivery_enabled ? "ON" : "OFF"}</span>
                    </label>
                    <input
                        data-testid="form-shop-free-delivery-above"
                        type="number"
                        className="inp max-w-[160px]"
                        value={form.free_delivery_above ?? ""}
                        disabled={!form.free_delivery_enabled}
                        onChange={(e) => setForm({ ...form, free_delivery_above: e.target.value === "" ? null : Number(e.target.value) })}
                        placeholder="Free above ₹ (e.g. 499)"
                    />
                </div>
                <span className="text-[11px] text-muted-foreground mt-1">
                    Waives the customer&apos;s delivery fee when the cart subtotal crosses this amount.
                    Zone-level free delivery (if enabled) overrides this shop setting. Rider still gets paid
                    the distance-based fee — the platform absorbs the gap.
                </span>
            </Field>

            {/* iter62 — Per-shop commission override (PERCENT or FLAT — one only) */}
            <Field full label={<span className="inline-flex items-center gap-1.5">Commission override (admin)</span>}>
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex rounded-xl border border-border overflow-hidden">
                        <button
                            data-testid="form-comm-kind-percent"
                            type="button"
                            onClick={() => setForm({ ...form, commission_override_kind: "PERCENT", commission_override_amount: "" })}
                            className={`px-3 py-2 text-xs font-semibold ${(form.commission_override_kind || "PERCENT") === "PERCENT" ? "bg-primary text-primary-foreground" : "bg-muted"}`}
                        >Percent</button>
                        <button
                            data-testid="form-comm-kind-flat"
                            type="button"
                            onClick={() => setForm({ ...form, commission_override_kind: "FLAT", commission_override_pct: "" })}
                            className={`px-3 py-2 text-xs font-semibold ${form.commission_override_kind === "FLAT" ? "bg-primary text-primary-foreground" : "bg-muted"}`}
                        >Flat ₹</button>
                    </div>
                    {(form.commission_override_kind || "PERCENT") === "PERCENT" ? (
                        <input
                            data-testid="form-comm-pct"
                            type="number"
                            step="0.01"
                            className="inp max-w-[180px]"
                            value={form.commission_override_pct}
                            onChange={(e) => setForm({ ...form, commission_override_pct: e.target.value })}
                            placeholder="% (e.g. 8.5)"
                        />
                    ) : (
                        <input
                            data-testid="form-comm-amount"
                            type="number"
                            step="0.01"
                            className="inp max-w-[180px]"
                            value={form.commission_override_amount}
                            onChange={(e) => setForm({ ...form, commission_override_amount: e.target.value })}
                            placeholder="Flat ₹ commission"
                        />
                    )}
                </div>
                <span className="text-[11px] text-muted-foreground mt-1">
                    Leave blank to inherit the tenant default. Only ONE of Percent / Flat applies at a time.
                    This value also drives the Admin → Pricing & Commission page for this shop.
                </span>
            </Field>

            {/* iter131 — Per-shop delivery-fee bearer override */}
            <Field full label={<span className="inline-flex items-center gap-1.5">Delivery fee bearer (override)</span>}>
                <div className="flex rounded-xl border border-border overflow-hidden w-fit">
                    <button
                        data-testid="form-df-bearer-inherit"
                        type="button"
                        onClick={() => setForm({ ...form, delivery_fee_borne_by: null })}
                        className={`px-3 py-2 text-xs font-semibold ${(form.delivery_fee_borne_by == null || form.delivery_fee_borne_by === "") ? "bg-primary text-primary-foreground" : "bg-muted"}`}
                    >Inherit tenant</button>
                    <button
                        data-testid="form-df-bearer-admin"
                        type="button"
                        onClick={() => setForm({ ...form, delivery_fee_borne_by: "ADMIN" })}
                        className={`px-3 py-2 text-xs font-semibold ${form.delivery_fee_borne_by === "ADMIN" ? "bg-primary text-primary-foreground" : "bg-muted"}`}
                    >Admin</button>
                    <button
                        data-testid="form-df-bearer-merchant"
                        type="button"
                        onClick={() => setForm({ ...form, delivery_fee_borne_by: "MERCHANT" })}
                        className={`px-3 py-2 text-xs font-semibold ${form.delivery_fee_borne_by === "MERCHANT" ? "bg-primary text-primary-foreground" : "bg-muted"}`}
                    >Merchant</button>
                </div>
                <span className="text-[11px] text-muted-foreground mt-1">
                    Overrides the tenant-wide delivery-fee bearer for THIS shop only. &quot;Inherit tenant&quot; uses the platform default.
                </span>
            </Field>
        </div>
    );
}
